<script lang="ts">
  // ミニキャラ演出 — ホイール幅の 1/4 サイズで、ホイール中央付近を動く小キャラ。
  // variant 1〜5 でアニメーションパターンを切り替える。

  import type { CharType } from '../lib/state.svelte.ts';

  interface Props {
    type: CharType | null;
    variant: number;
  }

  let { type, variant }: Props = $props();
</script>

{#key `${type}-${variant}`}
  {#if type}
    <div class="mini-wrap">
      <div class="mini variant-{variant}">
        {#if type === 'dragon'}
          <svg viewBox="0 0 64 56" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <ellipse cx="34" cy="36" rx="22" ry="11" fill="#2d8f3f"/>
            <path d="M28,28 Q18,8 6,16 Q18,22 26,30" fill="#3db05b" opacity="0.92"/>
            <circle cx="52" cy="28" r="9" fill="#2d8f3f"/>
            <ellipse cx="58" cy="31" rx="5" ry="3.5" fill="#3db05b"/>
            <circle cx="50" cy="24" r="3.5" fill="#ff8800"/>
            <circle cx="51" cy="23" r="2" fill="#1a1a00"/>
            <circle cx="52" cy="22" r="0.8" fill="white"/>
            <polygon points="50,16 47,7 53,16" fill="#1a6b2a"/>
            <rect x="20" y="44" width="5" height="9" rx="2.5" fill="#1a6b2a"/>
            <rect x="33" y="45" width="5" height="9" rx="2.5" fill="#1a6b2a"/>
            <rect x="44" y="44" width="5" height="9" rx="2.5" fill="#1a6b2a"/>
            <path d="M12,38 Q4,46 10,52 Q6,42 18,40" fill="#1a6b2a"/>
            <polygon points="26,26 28,18 30,26" fill="#1a6b2a"/>
            <polygon points="36,25 38,16 40,25" fill="#1a6b2a"/>
          </svg>
        {:else if type === 'rabbit'}
          <svg viewBox="0 0 48 64" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <ellipse cx="16" cy="14" rx="5" ry="13" fill="#f0f0f0"/>
            <ellipse cx="16" cy="14" rx="3" ry="11" fill="#ffb3c8"/>
            <ellipse cx="32" cy="14" rx="5" ry="13" fill="#f0f0f0"/>
            <ellipse cx="32" cy="14" rx="3" ry="11" fill="#ffb3c8"/>
            <circle cx="24" cy="30" r="13" fill="#f0f0f0"/>
            <circle cx="14" cy="33" r="3.5" fill="#ffb3c8" opacity="0.55"/>
            <circle cx="34" cy="33" r="3.5" fill="#ffb3c8" opacity="0.55"/>
            <circle cx="19" cy="27" r="2.8" fill="#cc2255"/>
            <circle cx="29" cy="27" r="2.8" fill="#cc2255"/>
            <circle cx="20" cy="26" r="1.3" fill="#220011"/>
            <circle cx="30" cy="26" r="1.3" fill="#220011"/>
            <ellipse cx="24" cy="33" rx="2" ry="1.3" fill="#ff88aa"/>
            <ellipse cx="24" cy="50" rx="14" ry="12" fill="#f0f0f0"/>
            <ellipse cx="14" cy="60" rx="5" ry="3" fill="#f0f0f0"/>
            <ellipse cx="34" cy="60" rx="5" ry="3" fill="#f0f0f0"/>
          </svg>
        {:else}
          <svg viewBox="0 0 56 64" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M8,38 Q8,10 28,10 Q48,10 48,38 L48,54 Q43,49 38,54 Q33,47 28,54 Q23,47 18,54 Q13,49 8,54 Z"
              fill="#8ec8e8" opacity="0.88"/>
            <ellipse cx="20" cy="32" rx="4.5" ry="6" fill="white"/>
            <ellipse cx="36" cy="32" rx="4.5" ry="6" fill="white"/>
            <circle cx="21" cy="34" r="3" fill="#1a3a5c"/>
            <circle cx="37" cy="34" r="3" fill="#1a3a5c"/>
            <circle cx="22" cy="32" r="1.2" fill="white"/>
            <circle cx="38" cy="32" r="1.2" fill="white"/>
            <path d="M18,46 Q22,52 28,46 Q34,52 38,46" stroke="#1a3a5c" stroke-width="2" fill="none" stroke-linecap="round"/>
          </svg>
        {/if}
      </div>
    </div>
  {/if}
{/key}

<style>
  /* ホイールセクション内に絶対配置: ホイール中央高さに来るように top:50% */
  .mini-wrap {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 0;
    pointer-events: none;
    z-index: 145;
    display: flex;
    justify-content: center;
  }

  /* ホイール幅の 1/4 = 25% (ユーザー要望) */
  .mini {
    width: 25%;
    aspect-ratio: 1;
    transform-origin: center;
    margin-top: -12.5%;  /* キャラ自身の高さの半分を上にオフセットして中央配置 */
    will-change: transform, opacity;
  }

  .mini :global(svg) {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 4px 8px rgba(0,0,0,0.4));
  }

  /* ──── V1 標準: 左外→右外、3 ホップ ──── */
  @keyframes mini-v1 {
    0%   { transform: translate(-220%, 0); opacity: 0; }
    10%  { transform: translate(-180%, 0); opacity: 1; }
    25%  { transform: translate(-100%, -50%); }
    40%  { transform: translate(-30%, 0); }
    55%  { transform: translate(40%, -50%); }
    70%  { transform: translate(110%, 0); }
    85%  { transform: translate(180%, -30%); opacity: 1; }
    100% { transform: translate(240%, 0); opacity: 0; }
  }
  .variant-1 { animation: mini-v1 1.8s cubic-bezier(0.45, 0, 0.55, 1) forwards; }

  /* ──── V2 高速直線: 一瞬で横切る ──── */
  @keyframes mini-v2 {
    0%   { transform: translate(-220%, 0) scale(0.8); opacity: 0; }
    10%  { transform: translate(-160%, 0) scale(1); opacity: 1; }
    90%  { transform: translate(160%, 0) scale(1); opacity: 1; }
    100% { transform: translate(220%, 0) scale(0.8); opacity: 0; }
  }
  .variant-2 { animation: mini-v2 1.0s linear forwards; }

  /* ──── V3 中央でジャンプ&ストップ: 中央で大きく止まって、消える ──── */
  @keyframes mini-v3 {
    0%   { transform: translate(-220%, 0) scale(0.5); opacity: 0; }
    20%  { transform: translate(-50%, -80%) scale(1.2); opacity: 1; }
    35%  { transform: translate(0, 0) scale(1.3); opacity: 1; }
    65%  { transform: translate(0, 0) scale(1.3); opacity: 1; }
    80%  { transform: translate(0, -30%) scale(1.1); opacity: 0.7; }
    100% { transform: translate(0, -120%) scale(0.6); opacity: 0; }
  }
  .variant-3 { animation: mini-v3 1.8s cubic-bezier(0.4, 0, 0.4, 1) forwards; }

  /* ──── V4 ジグザグ: 上下に大きく揺れながら横切る ──── */
  @keyframes mini-v4 {
    0%   { transform: translate(-220%, 0); opacity: 0; }
    8%   { transform: translate(-180%, -80%); opacity: 1; }
    23%  { transform: translate(-110%, 60%); }
    38%  { transform: translate(-40%, -80%); }
    53%  { transform: translate(30%, 60%); }
    68%  { transform: translate(100%, -80%); }
    83%  { transform: translate(170%, 60%); }
    100% { transform: translate(240%, 0); opacity: 0; }
  }
  .variant-4 { animation: mini-v4 1.6s ease-in-out forwards; }

  /* ──── V5 回転横切り: スピンしながら登場 → 中央停止 → 消える ──── */
  @keyframes mini-v5 {
    0%   { transform: translate(-220%, 0) rotate(0deg) scale(0); opacity: 0; }
    25%  { transform: translate(-50%, 0) rotate(360deg) scale(1.2); opacity: 1; }
    40%  { transform: translate(0, 0) rotate(540deg) scale(1.2); opacity: 1; }
    65%  { transform: translate(0, 0) rotate(540deg) scale(1.2); opacity: 1; }
    80%  { transform: translate(80%, 0) rotate(720deg) scale(1.1); opacity: 0.7; }
    100% { transform: translate(240%, 0) rotate(900deg) scale(0); opacity: 0; }
  }
  .variant-5 { animation: mini-v5 2.0s cubic-bezier(0.3, 0, 0.5, 1) forwards; }
</style>
